import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { Autocomplete, Input } from '@launchpad-ui/components';

import { PRESENT_VALUE_SENTINEL } from '../../../types/QueryBuilder';
import type { AutocompleteAction, FieldOptions, ParsedToken } from '../../../types/QueryBuilder';
import type { AutocompleteOption } from '../AutocompletePopover';
import { AutocompletePopover } from '../AutocompletePopover';
import { useQueryBuilderContext } from '../context';
import { useFilterBarContext } from '../FilterBarContext';
import { useAutocompleteNavigation } from '../hooks/useAutocompleteNavigation';
import { useKeyboardSelectionContext } from '../hooks/useKeyboardSelection';
import type { InlineFilterMatch } from '../utils';
import {
  bestMatchIndex,
  collectUsedExpressionTypes,
  dedupeOptionsByValue,
  expressionTypeAtOffset,
  fieldOptionsForScope,
  findAdjacentTextNavCell,
  flattenTokens,
  focusForTextEditing,
  focusQueryEdge,
  inferFieldType,
  INLINE_FILTER_PROMOTION_DELAY_MS,
  inlineFilterValueText,
  isPresentSentinelValue,
  matchExactFieldTrailingSpace,
  matchInlineFilter,
  matchScore,
  quoteFieldIfNeeded,
  replaceToken,
  scopeForExpressionType,
  takeLeadingOperatorDraft,
} from '../utils';

import styles from './FreeTextToken.module.css';

const FIELD_SEARCH_SYMBOLS = ['!**', '**', '!//', '//', '>=', '<=', '!=', '^=', '=^', '>', '<', '=', '*', '/', '^'];
const FIELD_SEARCH_WORDS = [
  'does not exist',
  'does not contain',
  'does not match',
  'is greater than or equal to',
  'is less than or equal to',
  'is greater than',
  'is less than',
  'not exists',
  'starts with',
  'ends with',
  'contains',
  'matches',
  'is not',
  'exists',
  'is',
];

function fieldKeySearchText(text: string): string {
  let body = text.trim();
  if (body.startsWith('!') && !/^![!=*/<>^]/.test(body)) {
    body = body.slice(1).trimStart();
  }
  const lower = body.toLowerCase();
  let cut = body.length;
  for (const symbol of FIELD_SEARCH_SYMBOLS) {
    const at = body.indexOf(symbol);
    if (at !== -1 && at < cut) {
      cut = at;
    }
  }
  for (const word of FIELD_SEARCH_WORDS) {
    const at = lower.indexOf(` ${word}`);
    if (at !== -1 && at < cut) {
      cut = at;
    }
  }
  return body.slice(0, cut).trim();
}

// Catalog lookups run at render, so the logic option tables are built inside the component.
function getLogicOptionDescriptions(): Record<string, string> {
  return {
    AND: msg('queryBuilder.filterBar.andDescription'),
    NOT: msg('queryBuilder.filterBar.notDescription'),
    OR: msg('queryBuilder.filterBar.orDescription'),
    GROUP: msg('queryBuilder.expressionGroup.logicalGroupDescription'),
  };
}

function getBaseLogicOptions(allowNotOperator: boolean) {
  const category = msg('queryBuilder.autocomplete.operatorsCategory');
  return [
    { value: 'AND', displayName: 'AND', category },
    { value: 'OR', displayName: 'OR', category },
    ...(allowNotOperator ? [{ value: 'NOT', displayName: 'NOT', category }] : []),
    { value: 'GROUP', displayName: msg('queryBuilder.expressionGroup.logicalGroup'), category },
  ];
}

/**
 * AND/OR join expressions — they are illegal with nothing (or only an open paren)
 * before the insertion point. UI-only guard; the grammar and payload builders are unchanged.
 */
function canInsertLogicOperatorAt(query: string, insertOffset: number): boolean {
  const before = query.slice(0, insertOffset).trimEnd();
  if (before.length === 0) {
    return false;
  }
  // Start of a parenthesised / typed group is still the beginning of an expression.
  return !before.endsWith('(') && !/(?:^|\s)NOT$/.test(before);
}

/** NOT starts an expression and is valid only where a field or group could begin. */
function canInsertNotAt(query: string, insertOffset: number): boolean {
  const before = query.slice(0, insertOffset).trimEnd();
  return before.length === 0 || before.endsWith('(') || /(?:^|\s)(?:AND|OR)$/.test(before);
}

const FREE_TEXT_SEARCH_SENTINEL = '__FREE_TEXT_SEARCH__';

const ACTIONS_SECTION = '__autocomplete_actions__';

const actionClaimsEnter = (action: AutocompleteAction, input: string) => action.isDefault?.(input) ?? true;
const FIELD_SEARCH_SECTION = '__free_text_search_section__';

function getAutocompleteCategoryPriority(): Map<string, number> {
  return new Map([
    [msg('queryBuilder.autocomplete.operatorsCategory'), 0],
    [msg('queryBuilder.autocomplete.relationalFieldsCategory'), 1],
  ]);
}

/**
 * Browsers that support `field-sizing: content` size the input from CSS (see FreeTextToken.module.css),
 * so the JS fallback below would only fight the cascade — skip it when the feature is available.
 */
const SUPPORTS_FIELD_SIZING =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('field-sizing', 'content');

/** Set an inline width that grows with the value. No-op when `field-sizing: content` is supported. */
function setInputWidth(el: HTMLInputElement | null, value: string): void {
  if (!el || SUPPORTS_FIELD_SIZING) {
    return;
  }
  // oxlint-disable-next-line no-param-reassign -- intentional DOM mutation on the passed-in input
  el.style.width = `${Math.max(value.length, 1)}ch`;
}

interface FreeTextTokenProps {
  initialValue?: string;
  /** Field options used to populate autocomplete suggestions. */
  options?: FieldOptions[];
  /** The parsed FREE_TEXT token — used to determine where to splice the query on selection. */
  parsedToken?: Extract<ParsedToken, { type: 'FREE_TEXT' }>;
  /**
   * Character offset in the query string where a selected filter should be inserted.
   * Used by auto-inserted FreeTextTokens (which have no parsedToken) to splice at the
   * correct position rather than appending to the end.
   */
  queryInsertOffset?: number;
  /** When true, the row expands with flex-grow to fill the remaining width of the filter bar. */
  isTrailing?: boolean;
  /**
   * Stable key identifying this token in the FilterBar grid.
   * Required for grid focus tracking and Shift+Arrow selection initiation.
   */
  tokenKey?: string;
  /** Enable the standalone unary NOT token for consumers whose query dialect supports it. */
  allowNotOperator?: boolean;
}

/**
 * Inline editable input for unstructured free-text within FilterBar.
 * - Suggests filter keys matching the word at the cursor
 * - Promotes a typed `field<operator>` prefix (`span_name=`, `span_name is`) to a FILTER token
 * - On Backspace with empty input, deletes the preceding token
 */
export function FreeTextToken({
  initialValue = '',
  options,
  parsedToken,
  queryInsertOffset,
  isTrailing = false,
  tokenKey,
  allowNotOperator = false,
}: FreeTextTokenProps) {
  const {
    wrapperRef,
    query,
    dispatch,
    parseQuery,
    setActivePopover,
    activePopoverRef,
    parsedQuery,
    getFieldOwner,
    parseClipboard,
  } = useQueryBuilderContext();
  const {
    gridState,
    inputId,
    showTypeLabels,
    allowExistsOption,
    relationalFieldOptions,
    omitFields,
    placeholder,
    autocompleteFooter,
    autocompleteHeader,
    autocompleteActions,
    autocompleteDetails,
    onFieldSearch,
  } = useFilterBarContext();

  // Detect whether this FreeText sits inside any EXPRESSION group — if so the
  // Relational Fields Group options are suppressed, since you can't nest a
  // relational group inside an existing one.
  const insideExpression = useMemo(() => {
    const offset: number | null = parsedToken ? parsedToken.location.start.offset : (queryInsertOffset ?? null);
    if (offset === null) {
      return false;
    }
    // Capture into a non-nullable local so the closure below can reference it
    // without TypeScript reverting to `number | null` (and without leaning on
    // forbidden non-null assertions).
    const targetOffset: number = offset;
    const visit = (tokens: ParsedToken[]): boolean => {
      for (const t of tokens) {
        if (t.type !== 'EXPRESSION') {
          continue;
        }
        if (targetOffset > t.location.start.offset && targetOffset < t.location.end.offset) {
          return true;
        }
        if (visit(t.inner)) {
          return true;
        }
      }
      return false;
    };
    return visit(parsedQuery);
  }, [parsedQuery, parsedToken, queryInsertOffset]);

  const fieldScope = useMemo(() => {
    const offset = parsedToken ? parsedToken.location.start.offset : queryInsertOffset;
    if (offset === undefined) {
      return undefined;
    }
    return scopeForExpressionType(expressionTypeAtOffset(parsedQuery, offset), relationalFieldOptions);
  }, [parsedQuery, parsedToken, queryInsertOffset, relationalFieldOptions]);

  // Scope field-key options to the group this FreeText sits in (e.g. inside
  // any_session(...) only session-scoped fields are offered).
  const scopedOptions = useMemo(
    () => (options === undefined ? undefined : fieldOptionsForScope(options, fieldScope)),
    [options, fieldScope],
  );

  // Derive the full LOGIC_OPTIONS list and GROUP_INSERT_TEXT lookup based on whether
  // the surrounding FilterBar configured any Relational Fields. Plain "Group" stays
  // in the Operators category; each relational option gets its own GROUP_<value>
  // entry under a "Relational Fields" category and inserts its prefixed `value()`.
  // When this FreeText sits inside an existing EXPRESSION the relational Group
  // entries are omitted — only the plain Group / AND / OR options remain available.
  const usedExpressionTypes = useMemo(() => collectUsedExpressionTypes(parsedQuery), [parsedQuery]);
  const { BASE_LOGIC_OPTIONS, LOGIC_OPTIONS, GROUP_INSERT_TEXT } = useMemo(() => {
    // Non-creatable options (creatable === false) still classify hydrated groups for scoping but
    // are withheld from the add-menu so no new group of that type can be created.
    const effectiveRelationalOptions = insideExpression
      ? []
      : (relationalFieldOptions ?? []).filter((o) => o.creatable !== false);
    const relationalGroupOptions = effectiveRelationalOptions.map((o) => ({
      value: `GROUP_${o.value}`,
      displayName: o.displayName ?? o.value,
      category: msg('queryBuilder.autocomplete.relationalFieldsCategory'),
      description: o.description ?? msg('queryBuilder.filterBar.relationalGroupDescription', { type: o.value }),
      // An expression of this type is already in the query — the user can't add a
      // second one with the same prefix, so disable the option to make that clear.
      ...(usedExpressionTypes.has(o.value) ? { disabled: true } : {}),
    }));
    const baseLogicOptions = getBaseLogicOptions(allowNotOperator);
    return {
      BASE_LOGIC_OPTIONS: baseLogicOptions,
      LOGIC_OPTIONS: [...baseLogicOptions, ...relationalGroupOptions],
      GROUP_INSERT_TEXT: {
        GROUP: '()',
        ...Object.fromEntries(effectiveRelationalOptions.map((o) => [`GROUP_${o.value}`, `${o.value}()`])),
      } as Record<string, string>,
    };
  }, [relationalFieldOptions, insideExpression, usedExpressionTypes, allowNotOperator]);
  const selCtx = useKeyboardSelectionContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const [inputValue, setInputValue] = useState(initialValue);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  // An inline `field<operator>` match whose operator could still grow (`>` → `>=`), held
  // back until the user pauses. `promoteInlineFilterRef` keeps the timer on the current
  // render's query rather than the one that scheduled it.
  const pendingPromotionRef = useRef<InlineFilterMatch | null>(null);
  const promotionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCellActivationRef = useRef<{
    tokenKey: string;
    part: 'op' | 'value';
    autoFocusFirst?: boolean;
    draft?: string;
  } | null>(null);
  const pendingTypedRef = useRef('');
  const cellActivationRafRef = useRef<number | null>(null);
  const cellActivationGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const promoteInlineFilterRef = useRef(promoteInlineFilter);
  const scopedOptionsRef = useRef(scopedOptions);
  scopedOptionsRef.current = scopedOptions;
  useEffect(() => {
    promoteInlineFilterRef.current = promoteInlineFilter;
  });
  useEffect(
    () => () => {
      if (promotionTimerRef.current !== null) {
        clearTimeout(promotionTimerRef.current);
      }
      if (cellActivationRafRef.current !== null) {
        cancelAnimationFrame(cellActivationRafRef.current);
      }
      if (cellActivationGraceTimerRef.current !== null) {
        clearTimeout(cellActivationGraceTimerRef.current);
      }
    },
    [],
  );

  // Sync external value changes into the uncontrolled input.
  // This allows the parsed token value to update the displayed text
  // without converting the input to fully controlled (which would
  // interfere with free typing).
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = initialValue;
      setInputWidth(inputRef.current, initialValue);
    }
    setInputValue(initialValue);
  }, [initialValue]);

  // Offset where a selected option would be spliced into the query — used to hide
  // AND/OR when this FreeText sits at the start of an expression.
  const logicInsertOffset = parsedToken ? parsedToken.location.start.offset : (queryInsertOffset ?? query.length);
  const allowsLogicOperator = canInsertLogicOperatorAt(query, logicInsertOffset);
  const allowsNotOperator = allowNotOperator && canInsertNotAt(query, logicInsertOffset);

  const popoverOptions = useMemo(() => {
    const lower = inputValue.toLowerCase().trim();

    // All Group-family options also surface whenever the input contains either paren
    // character — including the full pair `()` — since those are the visible delimiters
    // of a parenthesised group; AND/OR keep their letter-prefix match. An untrimmed-but-
    // non-empty input (e.g. only whitespace) falls back to the default no-filter behaviour.
    const logicOptions = LOGIC_OPTIONS.filter((o) => {
      if ((o.value === 'AND' || o.value === 'OR') && !allowsLogicOperator) {
        return false;
      }
      if (o.value === 'NOT' && !allowsNotOperator) {
        return false;
      }
      if (!lower) {
        return true;
      }
      if (o.value.startsWith('GROUP') && (lower.includes('(') || lower.includes(')'))) {
        return true;
      }
      // Match Group-family options by their displayed label too so typing "anyspan" or
      // "nospan" surfaces them alongside the letter-prefix matches on AND / OR / Group.
      if (o.displayName.toLowerCase().includes(lower)) {
        return true;
      }
      return o.value.toLowerCase().startsWith(lower);
    });

    const fieldOptions = scopedOptions
      ? (lower
          ? scopedOptions.filter(
              (o) => o.field.toLowerCase().includes(lower) || (o.displayValue ?? '').toLowerCase().includes(lower),
            )
          : scopedOptions
        ).map((o) => {
          let typeLabel: string | undefined;
          if (showTypeLabels) {
            if (o.valueType) {
              typeLabel = o.valueType;
            } else if (o.sampleValues && o.sampleValues.length > 0) {
              typeLabel = inferFieldType(o.sampleValues) === 'BOOLEAN' ? 'boolean' : 'string';
            } else {
              typeLabel = 'string';
            }
          }
          const category = o.fieldCategory ?? msg('queryBuilder.autocomplete.fieldsCategory');
          return {
            value: o.field,
            displayName: o.displayValue,
            iconName: o.iconName,
            description: o.description,
            typeLabel,
            category,
          };
        })
      : [];

    const combined = [...logicOptions, ...fieldOptions];
    if (lower) {
      combined.sort(
        (a, b) =>
          matchScore(a.displayName ?? String(a.value), lower) - matchScore(b.displayName ?? String(b.value), lower),
      );
    }
    // When the popover renders grouped sections, items must be contiguous by category so the
    // keyboard nav's flat-array index matches the visual rendered order (otherwise highlight jumps
    // between sections). Put expression-building choices before fields so AND/OR and relational
    // groups remain discoverable even when many field names also match the input. Other categories
    // remain alphabetical, and matchScore order stays stable within each category.
    const hasGrouping = combined.some((o) => 'category' in o && o.category);
    if (hasGrouping) {
      const AUTOCOMPLETE_CATEGORY_PRIORITY = getAutocompleteCategoryPriority();
      combined.sort((a, b) => {
        const aCat = 'category' in a && a.category ? a.category : '';
        const bCat = 'category' in b && b.category ? b.category : '';
        if (aCat === bCat) {
          return 0;
        }
        if (aCat === '') {
          return -1;
        }
        if (bCat === '') {
          return 1;
        }
        const aPriority = AUTOCOMPLETE_CATEGORY_PRIORITY.get(aCat) ?? Number.MAX_SAFE_INTEGER;
        const bPriority = AUTOCOMPLETE_CATEGORY_PRIORITY.get(bCat) ?? Number.MAX_SAFE_INTEGER;
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        return aCat.localeCompare(bCat);
      });
    }

    const trimmed = inputValue.trim();
    const availableActions = trimmed
      ? (autocompleteActions ?? []).filter((action) => action.isAvailable?.(trimmed) ?? true)
      : [];
    const actionOptions = availableActions.map((action) => ({
      value: action.id,
      displayName: action.label(trimmed),
      labelNode: action.renderLabel?.(trimmed),
      iconName: action.iconName,
      description: action.description,
      className: action.className,
      section: ACTIONS_SECTION,
    }));
    const hasActions = actionOptions.length > 0;
    const actionClaimsInput = availableActions.some((action) => actionClaimsEnter(action, trimmed));
    const fieldSearchOption = {
      value: FREE_TEXT_SEARCH_SENTINEL,
      displayName: msg('queryBuilder.filterBar.searchForField', { text: trimmed }),
      ...(hasActions ? { section: FIELD_SEARCH_SECTION } : {}),
    };
    const result = trimmed
      ? [...actionOptions, ...(actionClaimsInput ? [] : [fieldSearchOption]), ...combined]
      : combined;
    // Dedupe once so the rendered list, the arrow-key optionCount, and the Enter
    // onSelect index all agree — a field key can collide with a LOGIC_OPTIONS value
    // (or the search sentinel), which would otherwise crash the collection builder
    // and desync keyboard focus from selection.
    return dedupeOptionsByValue(result);
  }, [
    scopedOptions,
    inputValue,
    showTypeLabels,
    LOGIC_OPTIONS,
    allowsLogicOperator,
    allowsNotOperator,
    autocompleteActions,
  ]);

  const isOpen = isPopoverOpen && popoverOptions.length > 0;

  // Fields, relational groups, and logical operators can get a details panel here. The pinned
  // "Search for field" row has no catalog metadata and remains list-only.
  const renderDetails = useMemo(() => {
    if (!autocompleteDetails) {
      return undefined;
    }
    return (option: AutocompleteOption) => {
      const fieldOption = scopedOptions?.find((o) => o.field === option.value);
      if (fieldOption) {
        return autocompleteDetails({
          part: 'key',
          value: fieldOption.field,
          displayName: option.displayName,
          field: fieldOption.field,
          fieldOption,
        });
      }
      const relationalOption = relationalFieldOptions?.find((o) => `GROUP_${o.value}` === option.value);
      if (relationalOption) {
        return autocompleteDetails({
          part: 'relational',
          value: relationalOption.value,
          displayName: option.displayName,
          description: option.description,
          relationalFieldOption: relationalOption,
        });
      }
      const logicOption = BASE_LOGIC_OPTIONS.find((candidate) => candidate.value === option.value);
      return logicOption
        ? autocompleteDetails({
            part: 'logic',
            value: logicOption.value,
            displayName: logicOption.displayName,
            description: getLogicOptionDescriptions()[logicOption.value],
          })
        : null;
    };
  }, [autocompleteDetails, relationalFieldOptions, scopedOptions, BASE_LOGIC_OPTIONS]);

  /** Splice `insertText` in at this cell's position, and report the offset it landed on. */
  function computeInsertion(insertText: string): { newQuery: string; insertStart: number } {
    if (parsedToken) {
      return {
        newQuery: replaceToken(query, parsedToken, insertText),
        insertStart: parsedToken.location.start.offset,
      };
    }
    if (queryInsertOffset !== undefined) {
      const before = query.slice(0, queryInsertOffset);
      const after = query.slice(queryInsertOffset);
      // The query is already normalised, so the only adjustment is collapsing any
      // trailing spaces in `before`.
      const trimmedBefore = before.trimEnd();
      return {
        newQuery: `${before} ${insertText} ${after}`.trim().replace(/\s+/g, ' '),
        insertStart: trimmedBefore.length > 0 ? trimmedBefore.length + 1 : 0,
      };
    }
    return {
      newQuery: query ? `${query} ${insertText}` : insertText,
      insertStart: query ? query.length + 1 : 0,
    };
  }

  function clearInput() {
    if (inputRef.current) {
      inputRef.current.value = '';
      setInputWidth(inputRef.current, '');
    }
    setInputValue('');
  }

  function cancelPendingPromotion() {
    pendingPromotionRef.current = null;
    if (promotionTimerRef.current !== null) {
      clearTimeout(promotionTimerRef.current);
      promotionTimerRef.current = null;
    }
  }

  function clearPendingCellActivation() {
    pendingCellActivationRef.current = null;
    pendingTypedRef.current = '';
    if (cellActivationRafRef.current !== null) {
      cancelAnimationFrame(cellActivationRafRef.current);
      cellActivationRafRef.current = null;
    }
    if (cellActivationGraceTimerRef.current !== null) {
      clearTimeout(cellActivationGraceTimerRef.current);
      cellActivationGraceTimerRef.current = null;
    }
  }

  function cellInput(cellTokenKey: string, part: 'op' | 'value') {
    return (
      wrapperRef.current?.querySelector<HTMLInputElement>(
        `[data-token-container][data-key="${CSS.escape(cellTokenKey)}"] input[data-part="${CSS.escape(part)}"]`,
      ) ?? null
    );
  }

  function retargetPendingToValue(cellTokenKey: string) {
    const rest = pendingTypedRef.current;
    if (cellActivationRafRef.current !== null) {
      cancelAnimationFrame(cellActivationRafRef.current);
      cellActivationRafRef.current = null;
    }
    if (cellActivationGraceTimerRef.current !== null) {
      clearTimeout(cellActivationGraceTimerRef.current);
      cellActivationGraceTimerRef.current = null;
    }
    pendingCellActivationRef.current = { tokenKey: cellTokenKey, part: 'value' };
    pendingTypedRef.current = rest;
    scheduleCellActivation({ tokenKey: cellTokenKey, part: 'value' });
  }

  function replayPendingTyping() {
    const pending = pendingCellActivationRef.current;
    const text = pendingTypedRef.current;
    if (!pending || !text) {
      return;
    }
    if (pending.part === 'op') {
      const opInput = cellInput(pending.tokenKey, 'op');
      if (!opInput) {
        return;
      }
      const combined = opInput.value && !text.startsWith(opInput.value) ? `${opInput.value}${text}` : text;
      const split = takeLeadingOperatorDraft(combined);
      opInput.focus();
      opInput.value = split ? split.operatorText : combined;
      opInput.dispatchEvent(new Event('input', { bubbles: true }));
      if (!split || activePopoverRef.current?.part !== 'value') {
        return;
      }
      pendingTypedRef.current = split.rest;
      retargetPendingToValue(pending.tokenKey);
      return;
    }
    const valueInput = cellInput(pending.tokenKey, 'value');
    if (!valueInput) {
      return;
    }
    if (/^(?:=|!=|>=|<=|>|<)$/.test(text)) {
      pendingTypedRef.current = '';
      return;
    }
    valueInput.focus();
    valueInput.value = text;
    valueInput.dispatchEvent(new Event('input', { bubbles: true }));
    pendingTypedRef.current = '';
  }

  function armCellActivationGrace(pendingTokenKey: string, part: 'op' | 'value') {
    if (cellActivationRafRef.current !== null) {
      cancelAnimationFrame(cellActivationRafRef.current);
      cellActivationRafRef.current = null;
    }
    if (cellActivationGraceTimerRef.current !== null) {
      clearTimeout(cellActivationGraceTimerRef.current);
    }
    cellActivationGraceTimerRef.current = setTimeout(() => {
      if (
        pendingCellActivationRef.current?.tokenKey === pendingTokenKey &&
        pendingCellActivationRef.current?.part === part
      ) {
        pendingCellActivationRef.current = null;
      }
      cellActivationGraceTimerRef.current = null;
    }, 750);
  }

  function scheduleCellActivation(activation: {
    tokenKey: string;
    part: 'op' | 'value';
    autoFocusFirst?: boolean;
    draft?: string;
  }) {
    const typed = pendingTypedRef.current;
    clearPendingCellActivation();
    pendingTypedRef.current = typed;
    pendingCellActivationRef.current = activation;
    const startedAt = performance.now();

    let handedOff = false;
    const apply = () => {
      const pending = pendingCellActivationRef.current;
      if (!pending) {
        return;
      }
      const live = activePopoverRef.current;
      if (live?.tokenKey === pending.tokenKey && live.part === 'value' && pending.part === 'op') {
        handedOff = true;
        retargetPendingToValue(pending.tokenKey);
        return;
      }
      setActivePopover({
        tokenKey: pending.tokenKey,
        part: pending.part,
        ...(pending.autoFocusFirst ? { autoFocusFirst: true } : {}),
        ...(pending.draft !== undefined ? { draft: pending.draft } : {}),
      });
    };

    const targetInput = () =>
      wrapperRef.current?.querySelector<HTMLInputElement>(
        `[data-token-container][data-key="${CSS.escape(activation.tokenKey)}"] input[data-part="${CSS.escape(activation.part)}"]`,
      ) ?? null;

    const applyDraft = () => {
      if (activation.draft === undefined) {
        return;
      }
      const draftInput = targetInput();
      if (!draftInput) {
        return;
      }
      if (activation.draft === '' && (draftInput.value !== '' || pendingTypedRef.current !== '')) {
        return;
      }
      draftInput.value = activation.draft;
      const pos = activation.draft.length;
      draftInput.setSelectionRange(pos, pos);
    };

    const tick = () => {
      handedOff = false;
      apply();
      if (handedOff || pendingCellActivationRef.current?.part !== activation.part) {
        return;
      }
      const input = targetInput();
      if (input) {
        if (document.activeElement !== input) {
          input.focus();
        }
        if (document.activeElement === input) {
          applyDraft();
          if (activation.part === 'op' && input.value && !pendingTypedRef.current) {
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
          replayPendingTyping();
          if (
            activation.part === 'op' &&
            activePopoverRef.current?.tokenKey === activation.tokenKey &&
            activePopoverRef.current.part === 'value'
          ) {
            retargetPendingToValue(activation.tokenKey);
            return;
          }
          if (pendingCellActivationRef.current?.part !== activation.part) {
            return;
          }
          armCellActivationGrace(activation.tokenKey, activation.part);
          return;
        }
      }
      if (performance.now() - startedAt < 300) {
        cellActivationRafRef.current = requestAnimationFrame(tick);
        return;
      }
      apply();
      const lateInput = targetInput();
      lateInput?.focus();
      applyDraft();
      if (activation.part === 'op' && lateInput?.value && !pendingTypedRef.current) {
        lateInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      replayPendingTyping();
      if (
        activation.part === 'op' &&
        activePopoverRef.current?.tokenKey === activation.tokenKey &&
        activePopoverRef.current.part === 'value'
      ) {
        retargetPendingToValue(activation.tokenKey);
        return;
      }
      if (pendingCellActivationRef.current?.part !== activation.part) {
        return;
      }
      armCellActivationGrace(activation.tokenKey, activation.part);
    };

    cellActivationRafRef.current = requestAnimationFrame(tick);
  }

  function reassertPendingCellActivation(): boolean {
    let pending = pendingCellActivationRef.current;
    if (!pending) {
      return false;
    }
    const live = activePopoverRef.current;
    if (live?.tokenKey === pending.tokenKey && live.part === 'value' && pending.part === 'op') {
      pending = { tokenKey: pending.tokenKey, part: 'value' };
      pendingCellActivationRef.current = pending;
    }
    setActivePopover({
      tokenKey: pending.tokenKey,
      part: pending.part,
      ...(pending.autoFocusFirst ? { autoFocusFirst: true } : {}),
      ...(pending.draft !== undefined ? { draft: pending.draft } : {}),
    });
    requestAnimationFrame(() => {
      const input =
        wrapperRef.current?.querySelector<HTMLInputElement>(
          `[data-token-container][data-key="${CSS.escape(pending.tokenKey)}"] input[data-part="${CSS.escape(pending.part)}"]`,
        ) ?? null;
      if (input) {
        input.focus();
        if (document.activeElement === input) {
          armCellActivationGrace(pending.tokenKey, pending.part);
        }
      }
    });
    gridState?.setFocusedKey(pending.tokenKey);
    return true;
  }

  /**
   * Turn a `field<operator>` prefix the user typed into a lozenge, landing them on the
   * value cell so the rest of what they type fills the value in.
   */
  function promoteInlineFilter(match: InlineFilterMatch) {
    cancelPendingPromotion();
    if (redirectToFieldOwner(match.field)) {
      return;
    }
    // Resolve within this cell's scope first, matching the autocomplete path, so a same-named
    // field in another scope can't decide whether a typed comma separates values.
    const fieldData =
      scopedOptionsRef.current?.find((o) => o.field === match.field) ?? options?.find((o) => o.field === match.field);
    const valueText = inlineFilterValueText(match, fieldData);
    const insertText = `${match.negated ? '!' : ''}${quoteFieldIfNeeded(match.field)}:${match.operator}:${valueText}`;
    const { newQuery, insertStart } = computeInsertion(insertText);
    const isPresent = isPresentSentinelValue(match.value);

    const newFlatTokens = flattenTokens(parseQuery(newQuery));
    const newFilterFlatIndex = newFlatTokens.findIndex(
      (t) => t.type === 'FILTER' && t.field === match.field && t.location.start.offset === insertStart,
    );

    clearInput();
    dispatch({
      type: 'SET_QUERY',
      query: newQuery,
      ...(newFilterFlatIndex >= 0
        ? {
            focusOverride: {
              itemKey: String(newFilterFlatIndex),
              part: isPresent ? 'op' : 'value',
              caret: 'end',
              activate: true,
            },
          }
        : {}),
    });
    setIsPopoverOpen(false);
    if (newFilterFlatIndex >= 0) {
      scheduleCellActivation({ tokenKey: String(newFilterFlatIndex), part: isPresent ? 'op' : 'value' });
    }
  }

  function scheduleInlineFilterPromotion(text: string) {
    cancelPendingPromotion();
    const match = matchInlineFilter(text, scopedOptionsRef.current, allowExistsOption);
    if (!match) {
      return;
    }
    const holdForTyping = match.pending || (match.value !== '' && !isPresentSentinelValue(match.value));
    if (!holdForTyping) {
      promoteInlineFilter(match);
      return;
    }
    pendingPromotionRef.current = match;
    promotionTimerRef.current = setTimeout(() => {
      const queued = pendingPromotionRef.current;
      if (queued) {
        promoteInlineFilterRef.current(queued);
      }
    }, INLINE_FILTER_PROMOTION_DELAY_MS);
  }

  useEffect(() => {
    const text = inputRef.current?.value ?? '';
    if (!text.trim() || !scopedOptions?.length) {
      return;
    }
    const exactField = matchExactFieldTrailingSpace(text, scopedOptions);
    if (exactField) {
      promoteKnownField(exactField.field, {
        negated: exactField.negated,
        focusPart: 'op',
        emptyOpDraft: true,
      });
      return;
    }
    scheduleInlineFilterPromotion(text);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only when the option list identity changes; typing already schedules via onInput
  }, [scopedOptions, allowExistsOption]);

  function redirectToFieldOwner(fieldName: string): boolean {
    if (!omitFields?.includes(fieldName)) {
      return false;
    }
    const owner = getFieldOwner(fieldName);
    if (!owner) {
      return false;
    }
    if (inputRef.current) {
      inputRef.current.value = '';
      setInputWidth(inputRef.current, '');
    }
    setInputValue('');
    setIsPopoverOpen(false);
    requestAnimationFrame(() => owner.focus());
    return true;
  }

  function promoteKnownField(
    fieldName: string,
    promoteOptions: { negated?: boolean; focusPart: 'op' | 'value'; emptyOpDraft?: boolean } = {
      focusPart: 'value',
    },
  ) {
    if (redirectToFieldOwner(fieldName)) {
      return;
    }
    const fieldData = scopedOptions?.find((o) => o.field === fieldName) ?? options?.find((o) => o.field === fieldName);
    let defaultOp = '=';
    if (fieldData?.allowedOperators?.length) {
      defaultOp = fieldData.allowedOperators[0];
    } else if (fieldData?.valueType === 'number' || fieldData?.valueType === 'duration') {
      defaultOp = '>=';
    }
    const defaultValue = fieldData?.defaultToPresent ? PRESENT_VALUE_SENTINEL : '""';
    const insertText = `${promoteOptions.negated ? '!' : ''}${quoteFieldIfNeeded(fieldName)}:${defaultOp}:${defaultValue}`;
    const { newQuery, insertStart } = computeInsertion(insertText);

    const newFlatTokens = flattenTokens(parseQuery(newQuery));
    const newFilterFlatIndex = newFlatTokens.findIndex((t) => {
      if (t.type !== 'FILTER') {
        return false;
      }
      return t.field === fieldName && t.location.start.offset === insertStart;
    });

    clearInput();

    const nextPart = fieldData?.defaultToPresent ? 'op' : promoteOptions.focusPart;
    const spacePromotion = promoteOptions.focusPart === 'op' && !fieldData?.defaultToPresent;

    dispatch({
      type: 'SET_QUERY',
      query: newQuery,
      ...(newFilterFlatIndex >= 0
        ? {
            focusOverride: {
              itemKey: String(newFilterFlatIndex),
              part: nextPart,
              ...(spacePromotion ? { caret: 'end' as const } : {}),
            },
          }
        : {}),
    });
    setIsPopoverOpen(false);

    if (newFilterFlatIndex < 0) {
      return;
    }
    const targetKey = String(newFilterFlatIndex);
    if (fieldData?.defaultToPresent) {
      return;
    }
    if (spacePromotion) {
      scheduleCellActivation({
        tokenKey: targetKey,
        part: 'op',
        autoFocusFirst: true,
        ...(promoteOptions.emptyOpDraft !== false ? { draft: '' } : {}),
      });
      return;
    }
    scheduleCellActivation({ tokenKey: targetKey, part: 'value' });
  }

  function handleSelect(selected: string | number | boolean) {
    // Anchor `:focus-within` on the FilterBar before any state changes. The
    // mousedown that triggered this select can leave focus on the listbox
    // option (rendered in a popover portal *outside* `.filterBarOuter`), which
    // flips `:focus-within` off and removes the FilterBar's blue outline. The
    // outline returns once useApplyFocusOverride lands focus on the new
    // lozenge, but the gap is paint-visible — refocusing the input now keeps
    // the outline alive for the duration of the dispatch + re-render.
    inputRef.current?.focus();
    const selectedStr = String(selected);

    const action = autocompleteActions?.find((a) => a.id === selectedStr);
    if (action) {
      action.onSelect(inputValue.trim());
      setIsPopoverOpen(false);
      return;
    }

    if (selectedStr === FREE_TEXT_SEARCH_SENTINEL) {
      const fieldName = inputValue.trim();
      // The "Search for field" row is only offered when the input is non-empty; still
      // guard so a stale Enter path cannot insert a field-less `:=:""` free-text token.
      if (!fieldName) {
        return;
      }
      if (redirectToFieldOwner(fieldName)) {
        return;
      }
      const insertText = `${fieldName}:=:""`;
      const { newQuery, insertStart } = computeInsertion(insertText);

      const newFlatTokens = flattenTokens(parseQuery(newQuery));
      const newFilterFlatIndex = newFlatTokens.findIndex(
        (t) => t.type === 'FILTER' && t.field === fieldName && t.location.start.offset === insertStart,
      );

      clearInput();
      dispatch({
        type: 'SET_QUERY',
        query: newQuery,
        ...(newFilterFlatIndex >= 0 ? { focusOverride: { itemKey: String(newFilterFlatIndex), part: 'op' } } : {}),
      });
      setIsPopoverOpen(false);
      if (newFilterFlatIndex >= 0) {
        scheduleCellActivation({
          tokenKey: String(newFilterFlatIndex),
          part: 'op',
          autoFocusFirst: true,
          draft: '',
        });
      }
      return;
    }

    if (selectedStr in GROUP_INSERT_TEXT) {
      // Append an (optionally typed) parenthesised group at the appropriate insertion
      // point and route focus to the FreeText cell *inside* the new parens so the user
      // can immediately start typing a filter inside the group. The leading prefix
      // (anySpan / noSpan) is part of the inserted text — parseQuery picks it up via
      // the grammar's ExprPrefix token and surfaces it as `expressionType` on the
      // resulting EXPRESSION.
      //
      // Bail out if the typed prefix is already in use elsewhere in the query (the
      // option is rendered disabled; this catches the keyboard-Enter path that
      // bypasses react-aria's disabled handling).
      const prefixType = selectedStr.startsWith('GROUP_') ? selectedStr.slice('GROUP_'.length) : '';
      if (prefixType && usedExpressionTypes.has(prefixType)) {
        return;
      }
      const insertText = GROUP_INSERT_TEXT[selectedStr];
      const { newQuery, insertStart } = computeInsertion(insertText);

      // Locate the open paren of the newly inserted EXPRESSION in the re-parsed flat
      // list. The auto-inserted FreeText cell *inside* the parens has key
      // `ft-after-${openParenIndex}` (see FilterBar's buildAllKeys).
      const newFlatTokens = flattenTokens(parseQuery(newQuery));
      const newOpenParenIndex = newFlatTokens.findIndex(
        (t) => t.type === 'PAREN' && t.side === 'open' && t.expression.location.start.offset === insertStart,
      );

      clearInput();
      dispatch({
        type: 'SET_QUERY',
        query: newQuery,
        ...(newOpenParenIndex >= 0 ? { focusOverride: { itemKey: `ft-after-${newOpenParenIndex}` } } : {}),
      });
      setIsPopoverOpen(false);
      // The React Aria popover restores focus to its triggerRef (the originating
      // FreeText input) on close, racing with the focusOverride applied by
      // useApplyFocusOverride and landing focus back on the trigger. Re-apply the
      // focus on the next animation frames so our intent wins regardless of restore
      // order — and tolerate the case where React deferred the render past the first
      // frame by retrying on the second.
      if (newOpenParenIndex >= 0) {
        const targetKey = `ft-after-${newOpenParenIndex}`;
        const focusTarget = () =>
          document.querySelector<HTMLInputElement>(`input[data-key="${CSS.escape(targetKey)}"]`)?.focus();
        requestAnimationFrame(() => {
          focusTarget();
          requestAnimationFrame(focusTarget);
        });
      }
      return;
    }

    if (selectedStr === 'AND' || selectedStr === 'OR') {
      // Defence in depth: autocomplete already omits these at expression start, but
      // a stale focus index / keyboard path must not insert a leading operator.
      if (!allowsLogicOperator) {
        return;
      }
      // insertStart is where the operator landed, so we can identify it in the re-parsed flat list.
      const { newQuery, insertStart } = computeInsertion(selectedStr);

      const newFlatTokens = flattenTokens(parseQuery(newQuery));
      const newLogicIndex = newFlatTokens.findIndex(
        (t) => t.type === 'LOGIC_BOOL' && t.location.start.offset === insertStart,
      );

      clearInput();
      dispatch({
        type: 'SET_QUERY',
        query: newQuery,
        ...(newLogicIndex >= 0 ? { focusOverride: { itemKey: `ft-after-${newLogicIndex}` } } : {}),
      });
      setIsPopoverOpen(false);
      return;
    }

    if (selectedStr === 'NOT') {
      if (!allowsNotOperator) {
        return;
      }
      const { newQuery, insertStart } = computeInsertion(selectedStr);
      const newFlatTokens = flattenTokens(parseQuery(newQuery));
      const newNotIndex = newFlatTokens.findIndex(
        (token) => token.type === 'LOGIC_NOT' && token.location.start.offset === insertStart,
      );

      clearInput();
      dispatch({
        type: 'SET_QUERY',
        query: newQuery,
        ...(newNotIndex >= 0 ? { focusOverride: { itemKey: `ft-after-${newNotIndex}` } } : {}),
      });
      setIsPopoverOpen(false);
      // Closing autocomplete restores focus to the input that created NOT. Re-apply
      // the intended focus after that restore so the next field is inserted after
      // the NOT pill instead of before it.
      if (newNotIndex >= 0) {
        const targetKey = `ft-after-${newNotIndex}`;
        const focusTarget = () =>
          document.querySelector<HTMLInputElement>(`input[data-key="${CSS.escape(targetKey)}"]`)?.focus();
        requestAnimationFrame(() => {
          focusTarget();
          requestAnimationFrame(focusTarget);
        });
      }
      return;
    }

    const fieldName = selectedStr;
    promoteKnownField(fieldName, { focusPart: 'value' });
  }

  const actionOptionCount = useMemo(() => {
    const actionIds = new Set((autocompleteActions ?? []).map((action) => action.id));
    const firstNonAction = popoverOptions.findIndex((o) => !actionIds.has(String(o.value)));
    return firstNonAction < 0 ? popoverOptions.length : firstNonAction;
  }, [autocompleteActions, popoverOptions]);

  const suggestedFocusedIndex = useMemo(() => {
    const search = inputValue.trim();
    if (actionOptionCount > 0) {
      const leadingAction = autocompleteActions?.find((action) => action.id === String(popoverOptions[0].value));
      if (leadingAction && actionClaimsEnter(leadingAction, search)) {
        return 0;
      }
    }
    const firstNonAction = actionOptionCount < popoverOptions.length ? actionOptionCount : undefined;
    if (!search || !scopedOptions) {
      return actionOptionCount > 0 ? firstNonAction : undefined;
    }
    const fieldKeys = new Set(scopedOptions.map((o) => o.field));
    const index = bestMatchIndex(
      popoverOptions,
      search,
      (o) =>
        o.value !== FREE_TEXT_SEARCH_SENTINEL &&
        fieldKeys.has(String(o.value)) &&
        !('disabled' in o && o.disabled) &&
        !LOGIC_OPTIONS.some((logic) => logic.value === o.value),
    );
    if (index >= 0) {
      return index;
    }
    return actionOptionCount > 0 ? firstNonAction : undefined;
  }, [inputValue, popoverOptions, scopedOptions, LOGIC_OPTIONS, actionOptionCount, autocompleteActions]);

  const { focusedIndex, enterFocusedIndex, containerDataProps, handleKeyDown } = useAutocompleteNavigation({
    optionCount: popoverOptions.length,
    isOpen,
    onSelect: (index) => handleSelect(popoverOptions[index].value),
    onClose: () => setIsPopoverOpen(false),
    suggestedFocusedIndex,
  });

  const actionHighlighted = focusedIndex >= 0 && focusedIndex < actionOptionCount;

  const inputEl = (
    <Input
      ref={inputRef}
      className={isTrailing ? `${styles.trailingRow} ${styles.root}` : styles.root}
      defaultValue={initialValue}
      role="combobox"
      placeholder={isTrailing && !query.trim() ? placeholder : undefined}
      aria-label={msg('queryBuilder.filterBar.searchTermLabel')}
      aria-expanded={isOpen}
      tabIndex={0}
      aria-controls={listboxId}
      aria-autocomplete="list"
      id={isTrailing ? inputId : undefined}
      data-test-id={isTrailing ? 'query-builder-input' : undefined}
      data-freetext-token
      {...(inputValue ? { 'data-has-value': '' } : {})}
      {...(isTrailing ? { 'data-trailing': '' } : {})}
      {...containerDataProps}
      {...(tokenKey ? { 'data-key': tokenKey } : {})}
      onMouseDown={(e) => {
        // Toggle the autocomplete on every mousedown. Stop propagation (and the native
        // propagation) so the popover library's outside-pointerdown handler doesn't also
        // fire and race our toggle on the same press.
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        clearPendingCellActivation();
        setIsPopoverOpen((open) => !open);
      }}
      onFocus={(e) => {
        // Honor a one-shot suppress flag set by callers that programmatically
        // focus this input as a side effect of an action (e.g. LozengeFilter's
        // remove button moves focus to the preceding FreeText) — those flows
        // shouldn't auto-open the autocomplete. The flag is consumed on the
        // first focus event; a subsequent user-initiated mousedown still opens
        // the popover via the toggle handler above.
        const target = e.currentTarget;
        if (target.dataset.suppressAutocompleteOnFocus) {
          delete target.dataset.suppressAutocompleteOnFocus;
          if (tokenKey && gridState) {
            gridState.setFocusedKey(tokenKey);
          }
          return;
        }
        if (reassertPendingCellActivation()) {
          return;
        }
        setIsPopoverOpen(true);
        onFieldSearch?.(fieldKeySearchText(e.currentTarget.value), fieldScope);
        if (tokenKey && gridState) {
          gridState.setFocusedKey(tokenKey);
        }
      }}
      onBlur={() => {
        // Defer the close so a click on a popover option lands before the popover
        // unmounts, AND so a transient blur (e.g. react-aria restoring focus to a
        // sibling popover's trigger when its `isOpen` flips to false) doesn't close
        // a popover that has already regained focus by the time the RAF runs.
        requestAnimationFrame(() => {
          if (document.activeElement !== inputRef.current) {
            setIsPopoverOpen(false);
            // Leaving the cell abandons a half-typed filter rather than growing a
            // lozenge (and stealing focus) after the user has moved on.
            cancelPendingPromotion();
          }
        });
      }}
      onInput={(e) => {
        const { currentTarget } = e;
        const val = currentTarget.value;
        setInputWidth(currentTarget, val);
        setInputValue(val);
        if (pendingCellActivationRef.current) {
          pendingTypedRef.current += val;
          currentTarget.value = '';
          setInputValue('');
          setInputWidth(currentTarget, '');
          replayPendingTyping();
          return;
        }
        setIsPopoverOpen(true);
        onFieldSearch?.(fieldKeySearchText(val), fieldScope);

        // A space after a fully typed logical operator is the keyboard equivalent
        // of selecting it from autocomplete. Promote it immediately so the next
        // field starts in a clean free-text cell.
        if (/^NOT\s+$/i.test(val) && allowsNotOperator) {
          handleSelect('NOT');
          return;
        }
        const typedLogicOperator = /^(AND|OR)\s+$/i.exec(val)?.[1]?.toUpperCase();
        if ((typedLogicOperator === 'AND' || typedLogicOperator === 'OR') && allowsLogicOperator) {
          handleSelect(typedLogicOperator);
          return;
        }

        const typedGroup = val.trim();
        if (typedGroup === '(' || typedGroup === '()') {
          cancelPendingPromotion();
          handleSelect('GROUP');
          return;
        }

        cancelPendingPromotion();
        const exactField = matchExactFieldTrailingSpace(val, scopedOptionsRef.current);
        if (exactField) {
          promoteKnownField(exactField.field, {
            negated: exactField.negated,
            focusPart: 'op',
            emptyOpDraft: true,
          });
          return;
        }

        scheduleInlineFilterPromotion(val);
      }}
      onPaste={(e) => {
        const pastedText = e.clipboardData.getData('text/plain').trim();
        if (!pastedText) {
          return;
        }
        // Convert observability search syntax (`field=value`) to Lezer before parsing.
        // Intercept only when paste yields a FILTER or EXPRESSION (or AND/OR joining
        // them). LOGIC_NOT alone is not enough: an unconverted antlr fragment like
        // `NOT span_name=click` parses as NOT + FREE_TEXT and must not be committed
        // as the query (that left `span_name=click` as free text beside a stray
        // `:=:""` lozenge). Fall through so the input keeps the paste for editing.
        const insertText = parseClipboard(pastedText);
        const parsedPasted = parseQuery(insertText);
        const hasFilterOrGroup = parsedPasted.some(
          (t) => t.type === 'FILTER' || t.type === 'EXPRESSION' || t.type === 'LOGIC_BOOL',
        );
        if (!hasFilterOrGroup) {
          return;
        }

        e.preventDefault();

        const { newQuery, insertStart } = computeInsertion(insertText);

        // When the pasted content resolves to a single FILTER token, focus the FreeText
        // cell after it (preserves the previous single-filter UX). For richer pastes
        // (groups, multiple filters, etc.) skip the focus override and let DOM focus
        // remain on the trailing input.
        let focusOverrideAction: { itemKey: string } | undefined;
        if (parsedPasted.length === 1 && parsedPasted[0].type === 'FILTER') {
          const filterField = parsedPasted[0].field;
          const newFlatTokens = flattenTokens(parseQuery(newQuery));
          const newFilterFlatIndex = newFlatTokens.findIndex(
            (t) => t.type === 'FILTER' && t.field === filterField && t.location.start.offset === insertStart,
          );
          if (newFilterFlatIndex >= 0) {
            focusOverrideAction = { itemKey: `ft-after-${newFilterFlatIndex}` };
          }
        }

        clearInput();
        dispatch({
          type: 'SET_QUERY',
          query: newQuery,
          ...(focusOverrideAction ? { focusOverride: focusOverrideAction } : {}),
        });
        setIsPopoverOpen(false);
      }}
      onKeyDown={(e) => {
        // Enter commits a held-back inline filter (`span_name is`, still waiting to see
        // whether "not" follows) instead of falling through to a free-text search;
        // Escape drops it, matching the popover it dismisses.
        if (pendingPromotionRef.current) {
          if (e.key === 'Enter') {
            e.preventDefault();
            promoteInlineFilter(pendingPromotionRef.current);
            return;
          }
          if (e.key === 'Escape') {
            cancelPendingPromotion();
          }
        }

        if (e.key === 'Enter' && !inputValue.trim()) {
          e.preventDefault();
          dispatch({ type: 'COMMIT_QUERY', force: true });
          return;
        }

        if (isOpen && !(e.key === 'Tab' && actionHighlighted)) {
          handleKeyDown(e);
        }

        if (e.key === 'Enter' && isOpen && enterFocusedIndex < 0 && inputValue.trim()) {
          e.preventDefault();
          handleSelect(FREE_TEXT_SEARCH_SENTINEL);
          return;
        }

        // With no popover open, a plain Enter runs the search: a forced COMMIT_QUERY
        // fires onChange (→ onSubmit) even when the query is unchanged since the last
        // commit, so Enter always re-applies the query like the submit button.
        if (e.key === 'Enter' && !isOpen) {
          e.preventDefault();
          dispatch({ type: 'COMMIT_QUERY', force: true });
          return;
        }

        // Shift+Arrow: initiate token selection, but only when the cursor is already
        // at the boundary of the input (position 0 for left, value.length for right).
        // When mid-input, let the browser handle text selection normally.
        if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
          if (selCtx && tokenKey) {
            const input = e.currentTarget;
            const atBoundary =
              e.key === 'ArrowLeft'
                ? input.selectionStart === 0 && input.selectionEnd === 0
                : input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
            if (atBoundary) {
              e.preventDefault();
              selCtx.selectInDirection(e.key === 'ArrowLeft' ? 'left' : 'right', tokenKey);
              return;
            }
          }
        }

        // Ctrl/Cmd+Z: let the browser undo inside the input when it has uncommitted
        // text. Once the input is clean (inputValue === initialValue), propagate so
        // the FilterBar's onKeyDown can call undo() on the query history.
        if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
          if (inputValue !== initialValue) {
            e.stopPropagation();
          }
          return;
        }

        // Ctrl/Cmd+Shift+Z or Ctrl+Y (redo): mirror the undo branch — while the input
        // holds uncommitted text, let the browser redo inside it; once clean, propagate
        // so FilterBar's onKeyDown runs the query-history redo instead.
        if (
          (e.ctrlKey || e.metaKey) &&
          (((e.key === 'z' || e.key === 'Z') && e.shiftKey) || e.key === 'y' || e.key === 'Y')
        ) {
          if (inputValue !== initialValue) {
            e.stopPropagation();
          }
          return;
        }

        // Ctrl/Cmd+A: select all tokens in the grid when structured tokens exist.
        // When there are only FREE_TEXT keys (no structured tokens), let the browser
        // handle Cmd+A natively so the text inside the input gets selected.
        if ((e.ctrlKey || e.metaKey) && e.key === 'a' && selCtx) {
          const hasStructuredTokens = selCtx.allKeys.some((k) => !selCtx.isFreeTextKey(k));
          if (hasStructuredTokens) {
            e.preventDefault();
            selCtx.state.selectAll(selCtx.allKeys);
            return;
          }
          return;
        }

        const input = e.currentTarget;

        if (e.key === 'Backspace' && input.selectionStart === 0 && input.selectionEnd === 0 && selCtx && tokenKey) {
          const currentIdx = selCtx.allKeys.indexOf(tokenKey);
          for (let i = currentIdx - 1; i >= 0; i--) {
            if (!selCtx.isFreeTextKey(selCtx.allKeys[i])) {
              e.preventDefault();
              const key = selCtx.allKeys[i];
              const flatIdx = parseInt(key, 10);
              const flat = selCtx.flatTokens[flatIdx];
              if (!flat) {
                break;
              }
              if (flat.type === 'LOGIC_BOOL' || flat.type === 'LOGIC_NOT') {
                const operatorCell = (
                  input.closest<HTMLElement>('[role="grid"]') ?? wrapperRef.current
                )?.querySelector<HTMLElement>(
                  `[data-token-container][data-key="${CSS.escape(key)}"] [data-part="operator"]`,
                );
                const draft = flat.text.slice(0, -1);
                if (operatorCell && draft) {
                  focusForTextEditing(operatorCell, { caret: 'end', setActivePopover, draft });
                } else {
                  dispatch({
                    type: 'DELETE_TOKEN',
                    token: flat,
                    focusOverride: { itemKey: tokenKey, caret: 'start' },
                  });
                }
                break;
              }
              if (flat.type === 'PAREN') {
                dispatch({
                  type: 'UNWRAP_EXPRESSION',
                  token: flat.expression,
                  focusOverride: { itemKey: tokenKey, caret: 'start' },
                });
                break;
              }
              const token = flat;
              const text = token.text;
              if (text.length <= 1) {
                dispatch({ type: 'DELETE_TOKEN', token, focusOverride: { itemKey: tokenKey, caret: 'start' } });
                break;
              }
              const newQuery = replaceToken(query, token, text.slice(0, -1));
              if (token.type === 'FILTER') {
                const part = isPresentSentinelValue(token.value) ? 'op' : 'value';
                dispatch({
                  type: 'SET_QUERY',
                  query: newQuery,
                  focusOverride: { itemKey: key, part, caret: 'end', activate: true },
                });
              } else {
                dispatch({
                  type: 'SET_QUERY',
                  query: newQuery,
                  focusOverride: { itemKey: tokenKey, caret: 'start' },
                });
              }
              break;
            }
          }
          return;
        }

        // Use [role="grid"] (FilterBar in render-prop mode) as the search root,
        // falling back to wrapperRef (set by <QueryBuilder> in static-children mode).
        const searchRoot = input.closest<HTMLElement>('[role="grid"]') ?? wrapperRef.current;
        if (!searchRoot) {
          return;
        }

        //  - Cmd/Ctrl+←/→ jumps to the very start/end of the whole query.
        if ((e.metaKey || e.ctrlKey || e.altKey) && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
          const atBoundary =
            e.key === 'ArrowLeft'
              ? input.selectionStart === 0 && input.selectionEnd === 0
              : input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
          if (!atBoundary) {
            return;
          }
          const dir = e.key === 'ArrowLeft' ? 'left' : 'right';
          if (e.metaKey || e.ctrlKey) {
            if (focusQueryEdge(searchRoot, dir, setActivePopover)) {
              e.preventDefault();
            }
            return;
          }
          const adjacent = findAdjacentTextNavCell(searchRoot, input, dir);
          if (adjacent) {
            e.preventDefault();
            focusForTextEditing(adjacent, {
              caret: dir === 'left' ? 'end' : 'start',
              setActivePopover,
              word: dir,
            });
          }
          return;
        }

        if (e.key === 'ArrowLeft' && input.selectionStart === 0 && input.selectionEnd === 0) {
          const prev = findAdjacentTextNavCell(searchRoot, input, 'left');
          if (prev) {
            e.preventDefault();
            focusForTextEditing(prev, { caret: 'end', setActivePopover });
          }
        } else if (
          e.key === 'ArrowRight' &&
          input.selectionStart === input.value.length &&
          input.selectionEnd === input.value.length
        ) {
          const next = findAdjacentTextNavCell(searchRoot, input, 'right');
          if (next) {
            e.preventDefault();
            focusForTextEditing(next, { caret: 'start', setActivePopover });
          }
        }
      }}
    />
  );

  return (
    // Wrapping the input and the popover's ListBox under one react-aria Autocomplete gives the
    // ListBox virtual focus, so it stops pulling real DOM focus off the input when options load
    // asynchronously. disableAutoFocusFirst + no `filter` keep react-aria's own navigation and
    // filtering inert — useAutocompleteNavigation and the app-side option filtering stay authoritative.
    <Autocomplete inputValue={inputValue} onInputChange={setInputValue} disableAutoFocusFirst>
      {gridState ? (
        /* oxlint-disable-next-line
           jsx-a11y/click-events-have-key-events --
           role="row" is for grid semantics; the child <input> is the focusable
           element. Stop propagation so the FilterBar's outer click handler does
           not refocus a different input, and focus the spacer input when the
           click lands on row chrome (empty between-pill hit padding). */
        <div
          role="row"
          aria-label={msg('queryBuilder.filterBar.spaceLabel')}
          onClick={(e) => {
            e.stopPropagation();
            if (e.target !== inputRef.current) {
              inputRef.current?.focus();
            }
          }}
          {...(tokenKey ? { 'aria-selected': gridState.isSelected(tokenKey) } : {})}
        >
          <span role="gridcell" className={styles.container}>
            {inputEl}
          </span>
        </div>
      ) : (
        inputEl
      )}
      {isOpen && (
        <AutocompletePopover
          id={listboxId}
          triggerRef={inputRef}
          anchorKey={tokenKey ?? 'freetext'}
          isOpen
          onOpenChange={(open) => !open && setIsPopoverOpen(false)}
          onSelect={handleSelect}
          options={popoverOptions}
          groupByCategory={popoverOptions.some((o) => ('category' in o && o.category) || ('section' in o && o.section))}
          focusedIndex={focusedIndex}
          searchText={inputValue}
          footer={autocompleteFooter}
          header={autocompleteHeader}
          renderDetails={renderDetails}
        />
      )}
    </Autocomplete>
  );
}

FreeTextToken.displayName = 'FreeTextToken';
